import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Library, CheckCircle2, XCircle, FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const FormulaLibrary = () => {
  const { user } = React.useContext(AuthContext);
  const [formulas, setFormulas] = useState([]);
  const [activeTab, setActiveTab] = useState('reference');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  
  const [formulaData, setFormulaData] = useState({
    name: '',
    formula_type: 'reference',
    emulsion_type: 'H/E',
    product_type: '',
    supplier: '',
    source: '',
    notes: '',
    failure_type: '',
    observed_issues: '',
    stability_tests_results: '',
    composition: [],
  });

  useEffect(() => {
    fetchFormulas();
    fetchIngredients();
  }, [activeTab]);

  const fetchFormulas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/formulas?formula_type=${activeTab}`);
      setFormulas(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des formules');
    } finally {
      setLoading(false);
    }
  };

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      console.error('Error fetching ingredients');
    }
  };

  const addComposition = () => {
    setFormulaData({
      ...formulaData,
      composition: [
        ...formulaData.composition,
        { ingredient_id: '', ingredient_name: '', percentage: 0, percentage_min: null, percentage_max: null },
      ],
    });
  };

  const removeComposition = (index) => {
    setFormulaData({
      ...formulaData,
      composition: formulaData.composition.filter((_, i) => i !== index),
    });
  };

  const updateComposition = (index, field, value) => {
    const updated = [...formulaData.composition];
    updated[index][field] = value;
    
    if (field === 'ingredient_id') {
      const ing = ingredients.find((i) => i.id === value);
      updated[index].ingredient_name = ing ? ing.name : '';
    }
    
    setFormulaData({ ...formulaData, composition: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formulaData.composition.length === 0) {
      toast.error('Ajoutez au moins un ingredient a la composition');
      return;
    }

    try {
      await axios.post(`${API}/formulas`, formulaData);
      toast.success('Formule ajoutee avec succes !');
      setDialogOpen(false);
      resetForm();
      fetchFormulas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette formule ?')) return;

    try {
      await axios.delete(`${API}/formulas/${id}`);
      toast.success('Formule supprimee');
      fetchFormulas();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormulaData({
      name: '',
      formula_type: activeTab,
      emulsion_type: 'H/E',
      product_type: '',
      supplier: '',
      source: '',
      notes: '',
      failure_type: '',
      observed_issues: '',
      stability_tests_results: '',
      composition: [],
    });
  };

  const openAddDialog = (type) => {
    setFormulaData({ ...formulaData, formula_type: type });
    setDialogOpen(true);
  };

  const getEmulsionTypeBadge = (type) => {
    if (!type) return null;
    return (
      <span className="px-2 py-1 rounded-sm text-xs font-medium bg-[#2F4840]/10 text-[#2F4840]">
        {type}
      </span>
    );
  };

  const getStatusIcon = (formulaType) => {
    if (formulaType === 'stable') {
      return <CheckCircle2 className="h-5 w-5 text-[#346A53]" />;
    } else if (formulaType === 'unstable') {
      return <XCircle className="h-5 w-5 text-[#B94A3E]" />;
    }
    return <FileText className="h-5 w-5 text-[#5C615E]" />;
  };

  const renderFormulaCard = (formula) => {
    return (
      <Card key={formula.id} className="border-[#E5E5DF]" data-testid={`formula-card-${formula.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3 flex-1">
              {getStatusIcon(formula.formula_type)}
              <div className="flex-1">
                <h3 className="font-semibold text-[#1A1C1B] mb-1">{formula.name}</h3>
                <div className="flex gap-2 items-center flex-wrap">
                  {formula.emulsion_type && getEmulsionTypeBadge(formula.emulsion_type)}
                  {formula.product_type && (
                    <span className="text-xs text-[#5C615E]">{formula.product_type}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {formula.supplier && (
                <span className="text-xs text-[#5C615E] bg-[#E4EDE9] px-2 py-1 rounded-sm">
                  {formula.supplier}
                </span>
              )}
              {user?.role === 'admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(formula.id)}
                  className="border-[#E5E5DF] hover:bg-[#F6F7F6] text-[#B94A3E]"
                  data-testid={`delete-formula-${formula.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-[#5C615E] mb-2">
                Composition
              </h4>
              <div className="space-y-1">
                {formula.composition.map((comp, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm p-2 bg-[#F6F7F6] rounded-sm"
                  >
                    <span className="text-[#1A1C1B]">{comp.ingredient_name}</span>
                    <span className="font-medium mono-font text-[#2F4840]">
                      {comp.percentage_min && comp.percentage_max
                        ? `${comp.percentage_min}-${comp.percentage_max}%`
                        : `${comp.percentage}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {formula.notes && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#5C615E] mb-1">
                  Notes
                </h4>
                <p className="text-sm text-[#1A1C1B] p-2 bg-[#F6F7F6] rounded-sm">{formula.notes}</p>
              </div>
            )}

            {formula.failure_type && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#B94A3E] mb-1">
                  Type d'echec
                </h4>
                <p className="text-sm text-[#1A1C1B] p-2 bg-[#B94A3E]/10 rounded-sm border border-[#B94A3E]/20">
                  {formula.failure_type}
                </p>
              </div>
            )}

            {formula.observed_issues && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#B94A3E] mb-1">
                  Problemes observes
                </h4>
                <p className="text-sm text-[#1A1C1B] p-2 bg-[#B94A3E]/10 rounded-sm border border-[#B94A3E]/20">
                  {formula.observed_issues}
                </p>
              </div>
            )}

            {formula.stability_tests_results && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#346A53] mb-1">
                  Resultats tests de stabilite
                </h4>
                <p className="text-sm text-[#1A1C1B] p-2 bg-[#346A53]/10 rounded-sm border border-[#346A53]/20">
                  {formula.stability_tests_results}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-semibold heading-font text-[#1A1C1B]" data-testid="formulas-title">
            Bibliotheque de Formules
          </h2>
          <p className="text-[#5C615E] mt-2">
            Consulter et ajouter les formules de reference, stables et instables
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="reference" data-testid="tab-reference">
            <FileText className="h-4 w-4 mr-2" />
            Reference
          </TabsTrigger>
          <TabsTrigger value="stable" data-testid="tab-stable">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Stables
          </TabsTrigger>
          <TabsTrigger value="unstable" data-testid="tab-unstable">
            <XCircle className="h-4 w-4 mr-2" />
            Instables
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reference" className="space-y-4">
          <div className="flex justify-end">
            <Dialog
              open={dialogOpen && formulaData.formula_type === 'reference'}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => openAddDialog('reference')}
                  className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                  data-testid="add-reference-formula"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une formule de reference
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouvelle formule de reference</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="formula-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nom de la formule *</Label>
                      <Input
                        value={formulaData.name}
                        onChange={(e) => setFormulaData({ ...formulaData, name: e.target.value })}
                        required
                        className="border-[#E5E5DF]"
                        data-testid="formula-name-input"
                      />
                    </div>
                    <div>
                      <Label>Type d'emulsion</Label>
                      <Select
                        value={formulaData.emulsion_type}
                        onValueChange={(value) => setFormulaData({ ...formulaData, emulsion_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="H/E">Huile dans Eau (H/E)</SelectItem>
                          <SelectItem value="E/H">Eau dans Huile (E/H)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type de produit</Label>
                      <Input
                        value={formulaData.product_type}
                        onChange={(e) => setFormulaData({ ...formulaData, product_type: e.target.value })}
                        placeholder="Ex: Creme de jour, Lait corporel..."
                        className="border-[#E5E5DF]"
                      />
                    </div>
                    <div>
                      <Label>Fournisseur / Source</Label>
                      <Input
                        value={formulaData.supplier}
                        onChange={(e) => setFormulaData({ ...formulaData, supplier: e.target.value })}
                        className="border-[#E5E5DF]"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Composition *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addComposition}
                        className="border-[#E5E5DF]"
                        data-testid="add-composition"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ajouter ingredient
                      </Button>
                    </div>
                    <div className="text-xs text-[#5C615E] mb-2">
                      Vous pouvez saisir un % exact OU une plage (% min + % max)
                    </div>
                    <div className="space-y-2">
                      {formulaData.composition.map((comp, index) => (
                        <div key={index} className="flex gap-2 items-start p-3 bg-[#F6F7F6] rounded-sm">
                          <div className="flex-1">
                            <Select
                              value={comp.ingredient_id}
                              onValueChange={(value) => updateComposition(index, 'ingredient_id', value)}
                            >
                              <SelectTrigger data-testid={`composition-ingredient-${index}`}>
                                <SelectValue placeholder="Selectionner ingredient..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ingredients.map((ing) => (
                                  <SelectItem key={ing.id} value={ing.id}>
                                    {ing.name} ({ing.type})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage}
                              onChange={(e) => updateComposition(index, 'percentage', parseFloat(e.target.value))}
                              placeholder="% exact"
                              className="border-[#E5E5DF]"
                              data-testid={`composition-percentage-${index}`}
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_min || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_min', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% min"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_max || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_max', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% max"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeComposition(index)}
                            className="border-[#E5E5DF]"
                            data-testid={`remove-composition-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <textarea
                      value={formulaData.notes}
                      onChange={(e) => setFormulaData({ ...formulaData, notes: e.target.value })}
                      className="w-full min-h-[80px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                      placeholder="Notes additionnelles sur la formule..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                      data-testid="submit-formula-button"
                    >
                      Creer la formule
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="border-[#E5E5DF]"
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F4840]"></div>
            </div>
          ) : formulas.length === 0 ? (
            <Card className="border-[#E5E5DF]">
              <CardContent className="p-12 text-center">
                <Library className="h-12 w-12 text-[#5C615E] mx-auto mb-3" />
                <p className="text-[#5C615E]">
                  Aucune formule de reference disponible pour le moment.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {formulas.map((formula) => renderFormulaCard(formula))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="stable" className="space-y-4">
          <div className="flex justify-end">
            <Dialog
              open={dialogOpen && formulaData.formula_type === 'stable'}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => openAddDialog('stable')}
                  className="bg-[#346A53] hover:bg-[#2a5442] text-white"
                  data-testid="add-stable-formula"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une formule stable
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouvelle formule stable validee</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nom de la formule *</Label>
                      <Input
                        value={formulaData.name}
                        onChange={(e) => setFormulaData({ ...formulaData, name: e.target.value })}
                        required
                        className="border-[#E5E5DF]"
                      />
                    </div>
                    <div>
                      <Label>Type d'emulsion</Label>
                      <Select
                        value={formulaData.emulsion_type}
                        onValueChange={(value) => setFormulaData({ ...formulaData, emulsion_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="H/E">Huile dans Eau (H/E)</SelectItem>
                          <SelectItem value="E/H">Eau dans Huile (E/H)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Type de produit</Label>
                    <Input
                      value={formulaData.product_type}
                      onChange={(e) => setFormulaData({ ...formulaData, product_type: e.target.value })}
                      placeholder="Ex: Creme de jour, Lait corporel..."
                      className="border-[#E5E5DF]"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Composition *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addComposition}
                        className="border-[#E5E5DF]"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ajouter ingredient
                      </Button>
                    </div>
                    <div className="text-xs text-[#5C615E] mb-2">
                      Saisir un % exact OU une plage (% min + % max)
                    </div>
                    <div className="space-y-2">
                      {formulaData.composition.map((comp, index) => (
                        <div key={index} className="flex gap-2 items-start p-3 bg-[#F6F7F6] rounded-sm">
                          <div className="flex-1">
                            <Select
                              value={comp.ingredient_id}
                              onValueChange={(value) => updateComposition(index, 'ingredient_id', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selectionner ingredient..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ingredients.map((ing) => (
                                  <SelectItem key={ing.id} value={ing.id}>
                                    {ing.name} ({ing.type})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage}
                              onChange={(e) => updateComposition(index, 'percentage', parseFloat(e.target.value))}
                              placeholder="% exact"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_min || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_min', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% min"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_max || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_max', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% max"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeComposition(index)}
                            className="border-[#E5E5DF]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Resultats des tests de stabilite</Label>
                    <textarea
                      value={formulaData.stability_tests_results}
                      onChange={(e) => setFormulaData({ ...formulaData, stability_tests_results: e.target.value })}
                      className="w-full min-h-[80px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                      placeholder="J0: OK, J1: OK, Centrifugation: stable, 40C/7j: OK, 4C/7j: OK..."
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <textarea
                      value={formulaData.notes}
                      onChange={(e) => setFormulaData({ ...formulaData, notes: e.target.value })}
                      className="w-full min-h-[60px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                      placeholder="Notes additionnelles..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-[#346A53] hover:bg-[#2a5442] text-white">
                      Creer la formule
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="border-[#E5E5DF]"
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F4840]"></div>
            </div>
          ) : formulas.length === 0 ? (
            <Card className="border-[#E5E5DF]">
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-[#5C615E] mx-auto mb-3" />
                <p className="text-[#5C615E]">
                  Aucune formule stable enregistree pour le moment.
                </p>
                <p className="text-sm text-[#5C615E] mt-2">
                  Les formules validees apparaitront ici pour reference future.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {formulas.map((formula) => renderFormulaCard(formula))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="unstable" className="space-y-4">
          <div className="flex justify-end">
            <Dialog
              open={dialogOpen && formulaData.formula_type === 'unstable'}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => openAddDialog('unstable')}
                  className="bg-[#B94A3E] hover:bg-[#9a3d33] text-white"
                  data-testid="add-unstable-formula"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une formule instable
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouvelle formule instable (apprentissage)</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nom de la formule *</Label>
                      <Input
                        value={formulaData.name}
                        onChange={(e) => setFormulaData({ ...formulaData, name: e.target.value })}
                        required
                        className="border-[#E5E5DF]"
                      />
                    </div>
                    <div>
                      <Label>Type d'emulsion</Label>
                      <Select
                        value={formulaData.emulsion_type}
                        onValueChange={(value) => setFormulaData({ ...formulaData, emulsion_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="H/E">Huile dans Eau (H/E)</SelectItem>
                          <SelectItem value="E/H">Eau dans Huile (E/H)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Type de produit</Label>
                    <Input
                      value={formulaData.product_type}
                      onChange={(e) => setFormulaData({ ...formulaData, product_type: e.target.value })}
                      placeholder="Ex: Creme de jour, Lait corporel..."
                      className="border-[#E5E5DF]"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Composition *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addComposition}
                        className="border-[#E5E5DF]"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ajouter ingredient
                      </Button>
                    </div>
                    <div className="text-xs text-[#5C615E] mb-2">
                      Saisir un % exact OU une plage (% min + % max)
                    </div>
                    <div className="space-y-2">
                      {formulaData.composition.map((comp, index) => (
                        <div key={index} className="flex gap-2 items-start p-3 bg-[#F6F7F6] rounded-sm">
                          <div className="flex-1">
                            <Select
                              value={comp.ingredient_id}
                              onValueChange={(value) => updateComposition(index, 'ingredient_id', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selectionner ingredient..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ingredients.map((ing) => (
                                  <SelectItem key={ing.id} value={ing.id}>
                                    {ing.name} ({ing.type})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage}
                              onChange={(e) => updateComposition(index, 'percentage', parseFloat(e.target.value))}
                              placeholder="% exact"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_min || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_min', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% min"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={comp.percentage_max || ''}
                              onChange={(e) =>
                                updateComposition(index, 'percentage_max', e.target.value ? parseFloat(e.target.value) : null)
                              }
                              placeholder="% max"
                              className="border-[#E5E5DF]"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeComposition(index)}
                            className="border-[#E5E5DF]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Type d'echec</Label>
                    <Input
                      value={formulaData.failure_type}
                      onChange={(e) => setFormulaData({ ...formulaData, failure_type: e.target.value })}
                      placeholder="Ex: Cremage rapide, Dephasage a chaud, Blanchiment..."
                      className="border-[#E5E5DF]"
                    />
                  </div>

                  <div>
                    <Label>Problemes observes</Label>
                    <textarea
                      value={formulaData.observed_issues}
                      onChange={(e) => setFormulaData({ ...formulaData, observed_issues: e.target.value })}
                      className="w-full min-h-[80px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                      placeholder="Description detaillee des problemes observes..."
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <textarea
                      value={formulaData.notes}
                      onChange={(e) => setFormulaData({ ...formulaData, notes: e.target.value })}
                      className="w-full min-h-[60px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                      placeholder="Hypotheses sur les causes, actions correctives envisagees..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-[#B94A3E] hover:bg-[#9a3d33] text-white">
                      Creer la formule
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="border-[#E5E5DF]"
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F4840]"></div>
            </div>
          ) : formulas.length === 0 ? (
            <Card className="border-[#E5E5DF]">
              <CardContent className="p-12 text-center">
                <XCircle className="h-12 w-12 text-[#5C615E] mx-auto mb-3" />
                <p className="text-[#5C615E]">
                  Aucune formule instable enregistree pour le moment.
                </p>
                <p className="text-sm text-[#5C615E] mt-2">
                  Les formules avec problemes de stabilite seront repertoriees ici pour apprentissage.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {formulas.map((formula) => renderFormulaCard(formula))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FormulaLibrary;
